/** Preserve a tenant color when readable, otherwise select black or white. */
export function readableText(foreground, background) {
    const luminance = (hex) => {
        if (!/^#[0-9a-f]{6}$/i.test(hex))
            return null;
        const channels = [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16) / 255)
            .map(value => value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4));
        return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
    };
    const bg = luminance(background), fg = luminance(foreground);
    if (bg === null)
        return foreground;
    if (fg !== null && (Math.max(bg, fg) + 0.05) / (Math.min(bg, fg) + 0.05) >= 4.5)
        return foreground;
    return (bg + 0.05) / 0.05 >= 1.05 / (bg + 0.05) ? '#000000' : '#FFFFFF';
}
/**
 * The error red for text (a missed call, "Block number", "Delete", an error
 * line) on a background.
 *
 * readableText() turns a colour under 4.5:1 into white or black. The error
 * red #CE0F13 is 3.46:1 on the dark page, so in dark mode every red label was
 * white. This keeps the hue and saturation of the tenant red and moves only its
 * lightness, the smallest step that gives 7:1 (WCAG AAA) on the background:
 * a lighter red on dark (#F57275 on #0A0A1A), a darker red on light (#A80C0F
 * on #F5F5F7). Where no red reaches 7:1, the red nearest the tenant red at
 * 4.5:1 or more. Where no red reaches 4.5:1, black or white.
 *
 * Self-contained like readableText(): test harnesses evaluate it alone.
 */
export function errorText(error, background) {
    const valid = (hex) => /^#[0-9a-f]{6}$/i.test(hex);
    const luminance = (hex) => {
        const channels = [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16) / 255)
            .map(value => value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4));
        return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
    };
    const contrast = (a, b) => {
        const x = luminance(a), y = luminance(b);
        return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
    };
    if (!valid(background))
        return error;
    // Lighter on a dark background, darker on a light one: the side on which
    // readableText() picks white or black.
    const bg = luminance(background);
    const darkInk = (bg + 0.05) / 0.05 >= 1.05 / (bg + 0.05);
    if (!valid(error))
        return darkInk ? '#000000' : '#FFFFFF';
    if (contrast(error, background) >= 7)
        return error;
    const [r, g, b] = [1, 3, 5].map(index => parseInt(error.slice(index, index + 2), 16) / 255);
    const max = Math.max(r, g, b), min = Math.min(r, g, b), delta = max - min;
    const lightness = (max + min) / 2;
    const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
    const sector = delta === 0 ? 0
        : max === r ? ((((g - b) / delta) % 6) + 6) % 6
            : max === g ? (b - r) / delta + 2 : (r - g) / delta + 4;
    const fromLightness = (l) => {
        const chroma = (1 - Math.abs(2 * l - 1)) * saturation;
        const second = chroma * (1 - Math.abs(sector % 2 - 1));
        const rgb = [[chroma, second, 0], [second, chroma, 0], [0, chroma, second],
            [0, second, chroma], [second, 0, chroma], [chroma, 0, second]][Math.floor(sector) % 6];
        return '#' + rgb.map(value => Math.round(Math.min(1, Math.max(0, value + l - chroma / 2)) * 255)
            .toString(16).padStart(2, '0')).join('').toUpperCase();
    };
    const step = darkInk ? -0.01 : 0.01;
    let fallback = contrast(error, background) >= 4.5 ? error : '';
    for (let l = lightness + step; l > 0 && l < 1; l += step) {
        const candidate = fromLightness(l);
        const ratio = contrast(candidate, background);
        if (ratio >= 7)
            return candidate;
        if (!fallback && ratio >= 4.5)
            fallback = candidate;
    }
    return fallback || (darkInk ? '#000000' : '#FFFFFF');
}
